import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { useFeatureFlags } from "../hooks/useFeatureFlags";
import { decodeJWT } from "../utils/jwt";
import { Flag, X } from "lucide-react";

import OverviewTab from "./tabs/OverviewTab";
import FeaturesTab from "./tabs/FeaturesTab";
import JwtTab from "./tabs/JwtTab";
import JsonTab from "./tabs/JsonTab";

import TabBar from "./components/TabBar";

import Button from "../../../_components/ui/Button";

import "./FeatureFlagDebugger.scss";

const TABS = [
    {
        label: "Overview",
        value: "overview",
    },
    {
        label: "Features",
        value: "features",
    },
    {
        label: "JWT",
        value: "jwt",
    },
    {
        label: "JSON",
        value: "json",
    },
];

const FeatureFlagDebugger = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [isDismissed, setIsDismissed] = useState(false);
    const [activeTab, setActiveTab] = useState("overview");
    const [search, setSearch] = useState("");
    
    // Dragging state
    const [pos, setPos] = useState({ right: 18, bottom: 20 });
    const [isDragging, setIsDragging] = useState(false);

    const token = useSelector((state) => state.auth.token);
    const reduxFlags = useSelector((state) => state.auth.featureFlags);

    const {
        featureFlags,
        enabledFeatures,
        disabledFeatures,
    } = useFeatureFlags();

    const decodedToken = useMemo(
        () => (token ? decodeJWT(token) : null),
        [token]
    );

    const filteredEnabled = useMemo(
        () =>
            enabledFeatures.filter((feature) =>
                feature.toLowerCase().includes(search.toLowerCase())
            ),
        [enabledFeatures, search]
    );

    const filteredDisabled = useMemo(
        () =>
            disabledFeatures.filter((feature) =>
                feature.toLowerCase().includes(search.toLowerCase())
            ),
        [disabledFeatures, search]
    );

    // Development only
    if (process.env.NODE_ENV !== "development") {
        return null;
    }

    // Dismissal is plain component state, not persisted anywhere - a full
    // page reload remounts this component and resets it, so the debugger
    // comes back on every refresh rather than staying hidden forever.
    if (isDismissed) {
        return null;
    }

    const handlePointerDown = (e) => {
        // Only handle primary button (left click) or touch
        if (e.button !== undefined && e.button !== 0) return;

        e.preventDefault();
        
        const rect = e.currentTarget.getBoundingClientRect();
        const offsetX = e.clientX - rect.left;
        const offsetY = e.clientY - rect.top;

        let hasMoved = false;

        const handlePointerMove = (moveEvent) => {
            hasMoved = true;
            setIsDragging(true);
            
            setPos({
                left: moveEvent.clientX - offsetX,
                top: moveEvent.clientY - offsetY,
                right: "auto",
                bottom: "auto"
            });
        };

        const handlePointerUp = (upEvent) => {
            window.removeEventListener("pointermove", handlePointerMove);
            window.removeEventListener("pointerup", handlePointerUp);
            
            if (hasMoved) {
                // Snap to closest corner
                const centerX = upEvent.clientX;
                const centerY = upEvent.clientY;
                
                const halfWidth = window.innerWidth / 2;
                const halfHeight = window.innerHeight / 2;
                
                const newPos = {};
                
                // Left or Right
                if (centerX < halfWidth) {
                    newPos.left = 18;
                    newPos.right = "auto";
                } else {
                    newPos.right = 18;
                    newPos.left = "auto";
                }
                
                // Top or Bottom
                if (centerY < halfHeight) {
                    newPos.top = 20;
                    newPos.bottom = "auto";
                } else {
                    newPos.bottom = 20;
                    newPos.top = "auto";
                }
                
                setPos(newPos);
                
                // Delay resetting isDragging so onClick can read it as true and block
                setTimeout(() => setIsDragging(false), 50);
            }
        };

        window.addEventListener("pointermove", handlePointerMove);
        window.addEventListener("pointerup", handlePointerUp);
    };

    const dockStyle = {
        top: pos.top !== undefined && pos.top !== "auto" ? pos.top + 60 : "auto",
        bottom: pos.bottom !== undefined && pos.bottom !== "auto" ? pos.bottom + 60 : "auto",
        left: pos.left !== undefined && pos.left !== "auto" ? pos.left : "auto",
        right: pos.right !== undefined && pos.right !== "auto" ? pos.right : "auto",
    };

    return (
        <>
            {/* Floating Trigger */}

            <div
                className="ff-debugger__trigger-wrapper"
                style={{
                    ...pos,
                    transition: isDragging ? "none" : "all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)",
                }}
            >
                <Button
                    type="button"
                    variant="contained"
                    size="sm"
                    iconOnly
                    icon={<Flag size={16} />}
                    className="ff-debugger__trigger"
                    onClick={(e) => {
                        if (isDragging) {
                            e.preventDefault();
                            e.stopPropagation();
                            return;
                        }
                        setIsOpen((prev) => !prev);
                    }}
                    onPointerDown={handlePointerDown}
                    style={{ touchAction: "none" }}
                    title="Feature Flags DevTools"
                />

                <Button
                    type="button"
                    variant="ghost-danger"
                    size="xs"
                    iconOnly
                    icon={<X size={10} />}
                    className="ff-debugger__dismiss"
                    onClick={() => {
                        if (isDragging) return;
                        setIsDismissed(true);
                    }}
                    title="Hide feature flag debugger until next reload"
                    aria-label="Hide feature flag debugger"
                />
            </div>

            {/* Dock */}

            <aside
                className={`ff-debugger ${isOpen ? "ff-debugger--open" : ""}`}
                style={dockStyle}
            >
                {/* Header */}

                <header className="ff-debugger__header">
                    <h2>
                        <Flag size={16} />
                        Feature Flags
                    </h2>

                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        iconOnly
                        icon={<X size={16} />}
                        className="ff-debugger__close"
                        onClick={() => setIsOpen(false)}
                    />
                </header>

                {/* Tabs */}

                <TabBar
                    tabs={TABS}
                    activeTab={activeTab}
                    onChange={setActiveTab}
                />

                {/* Body */}

                <div className="ff-debugger__body">
                    {activeTab === "overview" && (
                        <OverviewTab
                            token={token}
                            reduxFlags={reduxFlags}
                            enabledCount={enabledFeatures.length}
                            disabledCount={disabledFeatures.length}
                        />
                    )}

                    {activeTab === "features" && (
                        <FeaturesTab
                            search={search}
                            onSearch={setSearch}
                            enabledFeatures={filteredEnabled}
                            disabledFeatures={filteredDisabled}
                        />
                    )}

                    {activeTab === "jwt" && (
                        <JwtTab token={decodedToken} />
                    )}

                    {activeTab === "json" && (
                        <JsonTab featureFlags={featureFlags} />
                    )}
                </div>

                {/* Footer */}

                <footer className="ff-debugger__footer">
                    <span>Feature Flags DevTools</span>

                    <span>
                        {Object.keys(featureFlags ?? {}).length} Flags
                    </span>
                </footer>
            </aside>
        </>
    );
};

export default FeatureFlagDebugger;